import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    ScrollAreaBarGeometryConstantType,
    ScrollAreaBarDynamicStyleType,
    ScrollAreaThumbConstantType,
    ScrollAreaBarGeometry,
    ScrollAreaThumb
} from "./type";
import type {
    Mutable
} from "../../../../types";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    getMotionTransition
} from "../../../../helpers/motion";
import {
    webStyle
} from "../../../../utils";

export const getScrollAreaBarGeometry = ({
    shapeConfigs,
    borders,
    axis
}: ScrollAreaBarGeometryConstantType): ScrollAreaBarGeometry => {
    const barSize = axis === "vertical" ? shapeConfigs.scrollBarWidth : shapeConfigs.scrollBarHeight;

    const idleThickness = barSize - borders.scrollBarThumbHover * 2;

    const trackThickness = idleThickness + borders.scrollBarThumb * 2;

    return {
        activeThickness: trackThickness - borders.scrollBarThumbActive * 2,
        activeInset: borders.scrollBarThumbActive,
        idleInset: borders.scrollBarThumb,
        trackThickness,
        idleThickness
    };
};

export const getScrollAreaThumb = ({
    contentLength,
    layoutLength,
    trackLength,
    minLength
}: ScrollAreaThumbConstantType): ScrollAreaThumb => {
    if(trackLength <= 0) {
        return {
            length: 0,
            travel: 0
        };
    }

    if(layoutLength <= 0 || contentLength <= layoutLength) {
        return {
            length: trackLength,
            travel: 0
        };
    }

    const length = Math.min(trackLength, Math.max(minLength, trackLength * layoutLength / contentLength));

    return {
        travel: trackLength - length,
        length
    };
};

const stylesheet = StyleSheet.create({
    track: {
        position: "absolute",
        ...webStyle({
            touchAction: "none",
            userSelect: "none"
        })
    },
    thumb: {
        pointerEvents: "none",
        position: "absolute"
    },
    horizontalTrack: {
        bottom: 0
    },
    horizontalThumb: {
        left: 0
    },
    verticalTrack: {
        end: 0
    },
    verticalThumb: {
        top: 0
    }
});

export const useStyles = ({
    isReduceMotionEnabled,
    isCornerReserved,
    shapeConfigs,
    thumbLength,
    isDragging,
    durations,
    isHovered,
    radiuses,
    borders,
    isShown,
    colors,
    axis
}: ScrollAreaBarDynamicStyleType) => {
    const geometry = getScrollAreaBarGeometry({
        shapeConfigs,
        borders,
        axis
    });

    const isActive = isHovered || isDragging;

    const thickness = isActive ? geometry.activeThickness : geometry.idleThickness;

    const inset = isActive ? geometry.activeInset : geometry.idleInset;

    const endInset = isCornerReserved ? geometry.idleInset + geometry.trackThickness : geometry.idleInset;

    let thumbRadius = radiuses.scrollBarThumb;

    let thumbColor = colors.content.icon.low;

    if(isHovered) {
        thumbRadius = radiuses.scrollBarThumbHover;

        thumbColor = colors.content.icon.mid;
    }

    if(isDragging) {
        thumbRadius = radiuses.scrollBarThumbActive;

        thumbColor = colors.content.icon.default;
    }

    const styles = {
        track: {
            pointerEvents: isShown ? "auto" : "none",
            opacity: isShown ? 1 : 0,
            ...webStyle(getMotionTransition({
                isReduceMotionEnabled,
                intent: "hover",
                properties: [
                    "opacity"
                ],
                durations
            }))
        } as Mutable<ViewStyle>,
        thumb: {
            ...getCornerStyle({
                radius: thumbRadius,
                shapeConfigs
            }),
            backgroundColor: thumbColor,
            ...webStyle(getMotionTransition({
                isReduceMotionEnabled,
                properties: [
                    "background-color"
                ],
                intent: "hover",
                durations
            }))
        } as Mutable<ViewStyle>
    };

    if(axis === "vertical") {
        Object.assign(styles.track, {
            width: geometry.trackThickness,
            top: geometry.idleInset,
            bottom: endInset
        });

        Object.assign(styles.thumb, {
            height: thumbLength,
            width: thickness,
            start: inset
        });

        return styles;
    }

    Object.assign(styles.track, {
        height: geometry.trackThickness,
        start: geometry.idleInset,
        end: endInset
    });

    Object.assign(styles.thumb, {
        width: thumbLength,
        height: thickness,
        top: inset
    });

    return styles;
};
export default stylesheet;
